import React from 'react';
import { Prose } from '../ui/Prose';
import { highlight } from '../../utils/highlight';
import { CONTACT } from '../../data/site';
import type { LegalDoc } from '../../types/legal';

/**
 * A stable id per clause, derived from its heading so the anchor is
 * readable and survives reordering. Exported because the "On this page"
 * list has to generate exactly the same ids.
 */
export const clauseId = (heading: string) => heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * The clauses of one legal document.
 *
 * The document title is the page `h1`, so clause headings are `h2`: they
 * are the top-level structure of the page, and someone navigating by
 * heading gets the clause list directly.
 *
 * Each clause is a linkable target, so a specific clause can be cited
 * rather than "see our terms".
 *
 * A clause with a null body is awaiting copy and renders as such. Which
 * clauses are outstanding is build state and lives in docs/registry.md,
 * but a silent gap on a legal page is worse than an honest one, so the
 * notice is shown in place rather than the clause being dropped.
 */
export function LegalDocBody({
  doc
}: {
  doc: LegalDoc;
}) {
  const published = doc.sections.filter(section => section.body !== null);
  const pending = doc.sections.filter(section => section.body === null);
  if (published.length === 0) {
    return <div data-component-id="C35" className="rounded-container border border-hairline bg-white p-6" data-id="element-31" data-mp="no7rr1636bvy">
        <h2 className="font-bebas text-3xl tracking-wide text-ink" data-id="element-32" data-mp="iouurnli3ofn">
          This one is being written
        </h2>
        <Prose className="mt-3" data-id="element-33" data-mp="xm5grqvrscpm">
          <p data-id="element-34" data-mp="snu5rctqnw5t">
            We are not publishing a version of it until it is accurate. If you
            have a question it would have answered, email us and we will answer
            it directly:{' '}
            <a href={`mailto:${CONTACT.email}`} className="font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-35" data-mp="2ufgpctbawos9">
              {CONTACT.email}
            </a>
            .
          </p>
        </Prose>
      </div>;
  }
  return <div data-component-id="C35" className="flex flex-col gap-10" data-id="element-36" data-mp="1xzo374f2101">
      {doc.intro && <Prose data-id="element-37" data-mp="6xc7tizzqrye">
          <p className="text-lg text-ink/85" data-id="element-38" data-mp="6vric7sj9z7e">{doc.intro}</p>
        </Prose>}

      <ol className="flex flex-col gap-10" data-id="element-39" data-mp="axmfr17cfsfw">
        {published.map((section, index) => <li key={section.heading} id={clauseId(section.heading)} className="scroll-mt-28 border-t border-hairline pt-5" data-id="element-40" data-mp="fomd27kdkcix" data-mpi={index}>
            <h2 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-41" data-mp="kq1wjv6wmaz7">
              <span className="mr-3 align-middle text-base font-bold text-flame" data-id="element-42" data-mp="pl7igu0b3pna">
                {index + 1}
              </span>
              {section.heading}
            </h2>
            <div className="mt-3" data-id="element-43" data-mp="2f60oviqr5b2l">
              <Prose data-id="element-44" data-mp="2f234yuwcofhn">
                <p data-id="element-45" data-mp="2a4th3jom0bvx">{highlight(section.body as string, section.highlight)}</p>
                {section.items && <ul className="ml-5 list-disc [&>li+li]:mt-2" data-id="element-46" data-mp="255gla9frfuh7">
                    {section.items.map(item => <li key={item} data-id="element-47" data-mp="208gkacxrphwy">{highlight(item, section.highlight)}</li>)}
                  </ul>}
              </Prose>
            </div>
          </li>)}
      </ol>

      {/* Named, not hidden. Someone reading an accessibility statement is
          entitled to know a section they might need is still coming. */}
      {pending.length > 0 && <div className="rounded-container border border-hairline bg-sand p-5" data-id="element-48" data-mp="2j59imnlsl8y5">
          <h2 className="font-bebas text-2xl tracking-wide text-ink" data-id="element-49" data-mp="3glon28ndb1rt">
            Still being written
          </h2>
          <Prose className="mt-2" data-id="element-50" data-mp="3btwvpvwe0s0m">
            <p data-id="element-51" data-mp="3bsvjho3mxzhb">
              {pending.length === 1 ? 'One section of this document is not published yet: ' : 'Some sections of this document are not published yet: '}
              <strong className="font-bold text-ink" data-id="element-52" data-mp="319f9hukncpae">
                {pending.map(section => section.heading).join(', ')}
              </strong>
              . We would rather leave it out than guess at it. If you need that
              answer now, email{' '}
              <a href={`mailto:${CONTACT.email}`} className="font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-53" data-mp="3f4hqbcr17z1z">
                {CONTACT.email}
              </a>{' '}
              or ring{' '}
              <a href={CONTACT.telephoneHref} className="font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-54" data-mp="1vv344ho9567">
                {CONTACT.telephone}
              </a>
              , and we will tell you over the telephone.
            </p>
          </Prose>
        </div>}
    </div>;
}